import React from 'react';

export type ProgramSlideTimerModalProps = {
  open: boolean;
  draftSeconds: number;
  onClose: () => void;
  onAddSeconds: (delta: number) => void;
  onReset: () => void;
  onStart: () => void | Promise<void>;
};

function formatDraftLabel(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  /* '1 분  10 초': 숫자↔단위는 한 칸, 분↔초 블록 사이만 두 칸(작은 간격) */
  return `${m} 분  ${s} 초`;
}

const ProgramSlideTimerModal: React.FC<ProgramSlideTimerModalProps> = ({
  open,
  draftSeconds,
  onClose,
  onAddSeconds,
  onReset,
  onStart,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="cp-program-slide-timer-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="cp-program-slide-timer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-slide-timer-modal-title"
        data-tutorial-slide="timer-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-program-slide-timer-modal-header">
          <h2 id="cp-slide-timer-modal-title" className="cp-program-slide-timer-modal-title">
            타이머
          </h2>
          <button
            type="button"
            className="cp-program-slide-timer-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            <svg
              className="cp-program-slide-timer-modal-close-icon"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M6 6l12 12M18 6L6 18"
              />
            </svg>
          </button>
        </div>
        <div className="cp-program-slide-timer-modal-presets" role="group" aria-label="시간 누적">
          <button type="button" className="cp-program-slide-timer-preset" onClick={() => onAddSeconds(300)}>
            5분
          </button>
          <button type="button" className="cp-program-slide-timer-preset" onClick={() => onAddSeconds(60)}>
            1분
          </button>
          <button type="button" className="cp-program-slide-timer-preset" onClick={() => onAddSeconds(10)}>
            10초
          </button>
          <button type="button" className="cp-program-slide-timer-preset cp-program-slide-timer-preset--reset" onClick={onReset}>
            리셋
          </button>
        </div>
        <div
          className={
            'cp-program-slide-timer-modal-display' +
            (draftSeconds <= 0 ? ' is-zero' : ' has-value')
          }
          aria-live="polite"
        >
          {formatDraftLabel(draftSeconds)}
        </div>
        <button
          type="button"
          className="cp-program-slide-timer-modal-start"
          data-tutorial-slide="timer-start"
          onClick={() => void Promise.resolve(onStart())}
          disabled={draftSeconds <= 0}
        >
          시작
        </button>
      </div>
    </div>
  );
};

export default ProgramSlideTimerModal;
